import React, { useEffect, useReducer } from 'react';

import _ from 'lodash';
import * as Yup from 'yup';
import { Dialog, DialogContent } from '@material-ui/core';
import { MenuItem, TextField, Tab, Tabs } from '@material-ui/core';

import API from 'utils/api';
import { useDialog, useSources } from 'hooks';
import { DialogActions, DialogTitle, ErrorDialog, Form } from 'components';
import { Grid, Row, Select, Spacer, TimePicker } from 'components';
import { VS } from 'components/Icons';
import { call, classes, randomKey } from 'utils';
import { toMoment } from 'utils/time';
import { getProviderFriendlyName } from '..';

import styles from './EditGameDialog.module.scss';

const initialState = { game: {}, teams: [], tab: 0, dialog: {} };

const reducer = (state, action) => {
  switch (action.type) {
    case 'INIT':
      return {
        ...state,
        game: action.payload.game || {},
        teams: action.payload.teams || [],
        formValues: initialValues(action.payload.game),
        formSession: randomKey(10),
        status: 'ready',
      };

    case 'RESET':
      return { ...initialState, status: 'init' };

    case 'LOADING':
      return { ...state, status: 'loading' };

    case 'TAB':
      return { ...state, tab: action.payload || 0 };

    case 'DIALOG':
      return { ...state, dialog: action.payload || {} };

    default:
      return state;
  }
};

const EditGameDialog = ({ open, onClose, gameId, leagueId, date }) => {
  const [state, dispatch] = useReducer(reducer, { ...initialState });
  const { status, game, teams, tab, dialog, formValues, formSession } = state;

  const sources = useSources();
  const homePosition = _.get(game, 'league.homePosition') || 'left';
  const isNew = !_.get(game, '_id');

  const { isOpening } = useDialog(open, { onOpen: () => dispatch({ type: 'RESET' }) });

  useEffect(() => {
    if (status === 'init' && (gameId || leagueId)) {
      dispatch({ type: 'LOADING' });
      sources.cancelAll('load');

      fetchData(gameId, leagueId, date, sources).then(data =>
        dispatch({ type: 'INIT', payload: data })
      );
    }
  }, [sources, status, gameId, leagueId]);

  if (isOpening || status !== 'ready') {
    return null;
  }

  const onSubmit = async (values, form) => {
    const { error } = game._id
      ? await API.editGame(game._id, values, sources)
      : await API.createGame(values, sources);

    form.setSubmitting(false);

    if (error) {
      dispatch({ type: 'DIALOG', payload: { error } });
    } else {
      call(onClose, values);
    }
  };

  const renderTeam = (type, form) => (
    <Select
      className={styles[type]}
      {...form.fieldProps(`${type}Team`, `${_.capitalize(type)} team`)}
      required
    >
      <MenuItem value="">
        <em>None</em>
      </MenuItem>

      {teams
        .filter(
          ({ _id }) => _id !== form.values[type === 'home' ? 'awayTeam' : 'homeTeam']
        )
        .map(({ _id, name }) => (
          <MenuItem key={_id} value={_id}>
            {name}
          </MenuItem>
        ))}
    </Select>
  );

  const renderProviderFooter = ({ updatedAt, gameId }) =>
    isNew ? null : (
      <div className={styles.footer}>
        <div>
          <span>Last update</span>
          <span>{updatedAt ? toMoment(updatedAt).format('LLL') : ''}</span>
        </div>

        <div>
          <span>Service ID</span>
          <span>{gameId || ''}</span>
        </div>
      </div>
    );

  const renderManualProvider = form => {
    const manual = (game.statistics || []).find(it => it.provider === 'manual') || {};
    const direction = homePosition === 'left' ? 'row' : 'row-reverse';
    const periodLabel = _.capitalize(game.league.periodName);
    const scoreProps = { type: 'number', inputProps: { min: 0, max: 999 } };

    return (
      <div className={classes(styles.provider, tab === 0 ? styles.visible : '')}>
        <Grid>
          <Row width={[5, 2, 2, 3]}>
            <div style={{ display: 'flex', flexDirection: direction }}>
              <TextField
                {...form.fieldProps('homeScore', 'Home score')}
                {...scoreProps}
              />
              <Spacer />
              <TextField
                {...form.fieldProps('awayScore', 'Away score')}
                {...scoreProps}
              />
            </div>

            <TextField {...form.fieldProps('period', periodLabel)} />
            <TextField {...form.fieldProps('clock', 'Clock')} />

            <Select {...form.fieldProps('status', 'Status')} required>
              <MenuItem value="scheduled">Scheduled</MenuItem>
              <MenuItem value="inprogress">In Progress</MenuItem>
              <MenuItem value="final">Final</MenuItem>
            </Select>
          </Row>
        </Grid>

        {renderProviderFooter(manual)}
      </div>
    );
  };

  const renderProvider = (data, index) => {
    const field = (label, value) => (
      <div className={styles.readonly}>
        <label>{label}</label>
        <div>{_.isNil(value) ? '' : value}</div>
      </div>
    );

    const direction = homePosition === 'left' ? 'row' : 'row-reverse';
    const periodLabel = _.capitalize(game.league.periodName);

    return (
      <div
        key={data.provider}
        className={classes(styles.provider, tab === index + 1 ? styles.visible : '')}
      >
        <Grid>
          <Row width={[5, 2, 2, 3]}>
            <div style={{ display: 'flex', flexDirection: direction }}>
              {field('Home score', _.toInteger(data.homeScore))}
              <Spacer />
              {field('Away score', _.toInteger(data.awayScore))}
            </div>

            {field(periodLabel, data.period || '')}
            {field('Clock', data.clock || '')}
            {field('Status', data.status || '')}
          </Row>
        </Grid>

        {renderProviderFooter(data)}
      </div>
    );
  };

  const renderStatistics = form => {
    const providers = (game.statistics || [])
      .filter(it => it.provider !== 'manual')
      .sort((a, b) => a.provider.localeCompare(b.provider));

    return (
      <div className={styles.statistics}>
        <Row width={[2, 1]} align="top">
          <div className={styles.section}>Statistics</div>

          {providers.length ? (
            <div style={{ marginTop: 20 }}>
              <Select {...form.fieldProps('activeProvider', 'Active Provider')} required>
                <MenuItem value="manual">Manual</MenuItem>

                {providers.map(({ provider }) => (
                  <MenuItem key={provider} value={provider}>
                    {getProviderFriendlyName(provider)}
                  </MenuItem>
                ))}
              </Select>
            </div>
          ) : null}
        </Row>

        {providers.length ? (
          <Tabs
            value={tab}
            onChange={(_e, tab) => dispatch({ type: 'TAB', payload: tab })}
          >
            <Tab label="Manual" />

            {providers.map(({ provider }) => (
              <Tab key={provider} label={getProviderFriendlyName(provider)} />
            ))}
          </Tabs>
        ) : null}

        <div className={styles.content}>
          {renderManualProvider(form)}
          {providers.map(renderProvider)}
        </div>
      </div>
    );
  };

  const renderContent = form => {
    return (
      <div className={styles.editGameDialog}>
        <div className={styles.league}>
          <span>{game.league.name}</span>
          {game.league.fullName ? <span>{`- ${game.league.fullName}`}</span> : null}
        </div>

        <div className={styles.date}>
          <span>{toMoment(form.values.scheduled).format('LL')}</span>

          <TimePicker
            value={form.values.scheduled}
            onChange={value => {
              if (value) {
                const hour = value.hour();
                const minute = value.minute();

                form.setFieldValue(
                  'scheduled',
                  toMoment(form.initialValues.scheduled)
                    .hour(hour)
                    .minute(minute)
                    .format()
                );
              }
            }}
          />

          <span>{`(${toMoment(form.values.scheduled).utc().format('LT')} UTC)`}</span>
        </div>

        <div className={styles.section}>Teams</div>
        <div className={classes(styles.teams, styles[homePosition])}>
          {renderTeam('home', form)}
          <div className={styles.vs}>
            <VS />
          </div>
          {renderTeam('away', form)}
        </div>

        {renderStatistics(form)}
      </div>
    );
  };

  const onDismis = () => call(onClose);

  return (
    <Dialog open={open} onClose={onDismis}>
      <DialogTitle onClose={onDismis}>{isNew ? 'Create game' : 'Edit game'}</DialogTitle>

      <Form
        validationSchema={validate}
        initialValues={formValues}
        onSubmit={onSubmit}
        session={formSession}
      >
        {form => {
          const disabled = !open || form.isSubmitting;
          const canSave = form.dirty && form.isValid;

          return (
            <>
              <DialogContent>{renderContent(form)}</DialogContent>

              <DialogActions
                actions={[
                  { label: 'cancel', onClick: onDismis, disabled },
                  { label: 'save', type: 'submit', disabled: disabled || !canSave },
                ]}
              />
            </>
          );
        }}
      </Form>

      <ErrorDialog
        open={!!dialog.error}
        onClose={() => dispatch({ type: 'DIALOG' })}
        message={dialog.error}
      />
    </Dialog>
  );
};

export default EditGameDialog;

const initialValues = game => {
  const statistics =
    (_.get(game, 'statistics') || []).find(it => it.provider === 'manual') || {};

  return {
    league: _.get(game, 'league._id'),
    scheduled: toMoment(_.get(game, 'scheduled') || new Date()).format(),
    activeProvider: _.get(game, 'activeProvider') || 'manual',
    homeTeam: _.get(game, 'homeTeam._id') || '',
    awayTeam: _.get(game, 'awayTeam._id') || '',
    homeScore: _.toInteger(statistics.homeScore),
    awayScore: _.toInteger(statistics.awayScore),
    period: statistics.period || '',
    clock: statistics.clock || '',
    status: statistics.status || 'scheduled',
  };
};

const validate = Yup.object().shape({
  league: Yup.string().required('Required'),
  scheduled: Yup.string().required('Required'),
  activeProvider: Yup.string().required('Required'),
  homeTeam: Yup.string().required('Required'),
  awayTeam: Yup.string().required('Required'),
  homeScore: Yup.number(),
  awayScore: Yup.number(),
  period: Yup.string(),
  clock: Yup.string(),
  status: Yup.string().required('Required'),
});

const fetchData = async (gameId, leagueId, date, sources) => {
  const payload = {};

  if (gameId) {
    const params = { populate: 'league teams', ignorePlays: true };
    const { data } = await API.getGame(gameId, params, sources, 'load');

    if (data) {
      payload.game = data;
    }
  } else if (leagueId) {
    const params = { select: 'name fullName homePosition periodName' };
    const { data } = await API.getLeague(leagueId, params, sources, 'load');

    if (data) {
      payload.game = { league: data, scheduled: date };
    }
  }

  if (payload.game) {
    const { data } = await API.getTeams(
      {
        query: JSON.stringify({ leagues: payload.game.league._id }),
        select: 'name',
        sort: 'name',
      },
      sources,
      'load'
    );

    if (data && data.docs) {
      payload.teams = data.docs;
    }
  }

  return payload;
};
